import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, fmtDate, StatusBadge, Th, Td, DateRangeFilter, rangeParams, IpTag, LeadTag, hasPendingLead } from "../components/shared";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { JourneyDialog } from "./Customers";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

export default function Conversions() {
  const { user } = useAuth();
  const owner = !!user?.is_owner;
  const [tick, setTick] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [networks, setNetworks] = useState([]);
  const [filters, setFilters] = useState({ network_id: "", status: "", search: "" });
  const [rangeSel, setRangeSel] = useState({ range: "today" });
  const [page, setPage] = useState(0);
  const [journeyClick, setJourneyClick] = useState(null);
  const limit = 25;

  const load = useCallback(() => {
    const p = new URLSearchParams({ ...filters, limit, skip: page * limit });
    api.get(`/conversions?${p}&${rangeParams(rangeSel)}`).then((r) => setData(r.data));
  }, [filters, page, rangeSel, tick]);

  useEffect(() => {
    if (!hasPendingLead(data.items)) return;
    const t = setTimeout(() => setTick((x) => x + 1), 15000);
    return () => clearTimeout(t);
  }, [data]);

  useEffect(() => { api.get("/networks").then((r) => setNetworks(r.data)); }, []);
  useEffect(load, [load]);

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams({ ...filters, limit: 100000, skip: 0 });
      const res = await api.get(`/conversions?${p}&${rangeParams(rangeSel)}`);
    const items = res.data.items;
    const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["timestamp", "customer_name", "customer_email", "tx_id", "click_id", "offer", "network", "campaign", "payout", "status", "country"]];
    items.forEach((c) => rows.push([c.timestamp, c.customer_name || "", c.email_id || "", c.tx_id, c.click_id, c.offer_name, c.network_name, c.campaign_name, c.payout, c.status, c.country]));
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "omnilink_conversions.csv";
    a.click();
    toast.success(`CSV exported — ${items.length.toLocaleString()} conversions`);
    } catch {
      toast.error("Export failed — please try again");
    }
  };

  return (
    <div data-testid="conversions-page">
      <PageHeader
        title="Conversions Log"
        subtitle={`${data.total.toLocaleString()} conversions received via S2S postback`}
        action={<Button data-testid="export-conversions-button" variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>}
      />
      <div className="mb-4 fade-up">
        <DateRangeFilter value={rangeSel} onChange={(v) => { setRangeSel(v); setPage(0); }} />
      </div>
      <div className="flex flex-wrap gap-3 mb-4 fade-up">
        <Input data-testid="conversions-search-input" placeholder="Search click ID or tx ID…" value={filters.search} onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(0); }} className="max-w-xs bg-white" />
        <Select value={filters.network_id || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, network_id: v === "all" ? "" : v })); setPage(0); }}>
          <SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All networks" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All networks</SelectItem>{networks.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => { setFilters((f) => ({ ...f, status: v === "all" ? "" : v })); setPage(0); }}>
          <SelectTrigger data-testid="conversions-status-filter" className="w-40 bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{["approved", "pending", "rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Time</Th><Th>Customer</Th><Th>Tx ID</Th><Th>Click ID</Th><Th>Offer</Th><Th>Network</Th><Th>Campaign</Th><Th>Geo</Th>{owner && <Th>IP Check</Th>}<Th className="text-right">Payout</Th><Th>Status</Th></tr></thead>
            <tbody data-testid="conversions-table-body">
              {data.items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-indigo-50/40 cursor-pointer" onClick={() => setJourneyClick(c.click_id)}>
                  <Td className="whitespace-nowrap text-xs">{fmtDate(c.timestamp)}</Td>
                  <Td>
                    <div className="text-xs font-medium text-slate-900">{c.customer_name || "—"}</div>
                    <code className="text-[10px] text-indigo-600">{c.email_id || "anonymous"}</code>
                    <div><LeadTag lead={c.lead_db} testId={`conversion-lead-${c.id}`} /></div>
                  </Td>
                  <Td><code className="text-[11px]">{c.tx_id}</code></Td>
                  <Td><code className="text-[11px] text-indigo-600">{c.click_id}</code></Td>
                  <Td className="text-xs font-medium">{c.offer_name}</Td>
                  <Td className="text-xs">{c.network_name}</Td>
                  <Td className="text-xs">{c.campaign_name}</Td>
                  <Td className="font-mono text-xs">{c.country}</Td>
                  {owner && <Td><IpTag stacked check={c.ip_check} ip={c.ip} testId={`conversion-ip-${c.id}`} /></Td>}
                  <Td className="text-right font-mono font-semibold text-emerald-600">{money(c.payout)}</Td>
                  <Td><StatusBadge status={c.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-400">Page {page + 1} of {Math.max(1, Math.ceil(data.total / limit))}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="sm" className="h-7" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </Card>
      <JourneyDialog clickId={journeyClick} onClose={() => setJourneyClick(null)} />
    </div>
  );
}
